import "@xyflow/react/dist/style.css";

import {
  ReactFlow,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from "@xyflow/react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Maximize2, User, X } from "lucide-mui";
import { keys } from "../../api/queryKeys.ts";
import { api } from "../../api/client.ts";
import type { CompanyEdge, OwnershipTreeNode } from "../../api/client.ts";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { titleCase } from "../../utils/format.ts";

interface CompanyRelationsGraphProps {
  edges: CompanyEdge[];
  companyName: string;
  companyId: string;
}

interface FocalNodeData extends Record<string, unknown> {
  label: string;
  faded?: boolean;
}

interface RelatedNodeData extends Record<string, unknown> {
  name: string;
  roleTitle: string;
  active: boolean;
  ownershipPct: string | null;
  ownershipPctColor: string;
  relatedCompanyId: string | null;
  entityType: "company" | "person";
  faded?: boolean;
  focused?: boolean;
  onNavigate?: () => void;
}

interface DividerNodeData extends Record<string, unknown> {
  width: number;
}

const FADED_OPACITY = 0.18;

const FOCAL_W = 220;
const FOCAL_H = 56;
const RELATED_W = 190;
const RELATED_H = 62;
const H_GAP = 16;
const INTRA_GAP = 14; // vertical gap between sub-rows within the same depth band
const DEPTH_GAP = 64; // vertical gap between depth bands (clear separation)
const TOP_PAD = 20;

const OWNERSHIP_TYPES = new Set(["SHAREHOLDER_OF", "SUBSIDIARY_OF", "INVESTOR_IN"]);
const MUTED_TYPES = new Set(["AUDITOR", "ACCOUNTANT", "MAIN_ENTERPRISE_CONTACT"]);

function edgeColor(relationType: string): string {
  if (OWNERSHIP_TYPES.has(relationType)) return "var(--mui-palette-success-main)";
  if (MUTED_TYPES.has(relationType)) return "var(--mui-palette-text-disabled)";
  return "var(--mui-palette-primary-main)";
}

function formatRelationType(role: string, subtype?: string): string {
  const base = titleCase(role);
  if (subtype && subtype !== "" && role === "SHAREHOLDER_OF") {
    const sub = titleCase(subtype);
    return `${base} (${sub})`;
  }
  return base;
}

function nodeName(e: CompanyEdge): string {
  return e.related_contact_name ?? e.related_company_name ?? "Unknown";
}

function directOwnershipPct(e: CompanyEdge): string | null {
  if (e.relation_type !== "SHAREHOLDER_OF" || e.ownership_fraction == null) return null;
  return `${(e.ownership_fraction * 100).toFixed(1)}%`;
}


function FocalNode({ data }: NodeProps<Node<FocalNodeData>>) {
  return (
    <Box
      sx={{
        width: FOCAL_W,
        height: FOCAL_H,
        px: "12px",
        py: "8px",
        borderRadius: "8px",
        border: "2px solid",
        borderColor: "primary.main",
        bgcolor: "var(--mui-palette-primary-main)",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        outline: "none",
        opacity: data.faded ? FADED_OPACITY : 1,
        transition: "opacity 120ms",
      }}
    >
      <Handle id="top-t" type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: "none" }} />
      <Handle id="bottom-s" type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: "none" }} />
      <Typography
        sx={{
          color: "white",
          fontWeight: 700,
          fontSize: "0.85rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
          textAlign: "center",
        }}
      >
        {data.label}
      </Typography>
    </Box>
  );
}

// Layer a translucent tint over the solid paper background so edges
// routed beneath the node body don't show through.
const ENTITY_STYLES = {
  company: {
    background:
      "linear-gradient(rgba(var(--mui-palette-primary-mainChannel) / 0.06), rgba(var(--mui-palette-primary-mainChannel) / 0.06)), var(--mui-palette-background-paper)",
    iconColor: "primary.main",
  },
  person: {
    background:
      "linear-gradient(rgba(var(--mui-palette-secondary-mainChannel) / 0.06), rgba(var(--mui-palette-secondary-mainChannel) / 0.06)), var(--mui-palette-background-paper)",
    iconColor: "secondary.main",
  },
} as const;

function RelatedNode({ data }: NodeProps<Node<RelatedNodeData>>) {
  const entityStyle = ENTITY_STYLES[data.entityType];
  const Icon = data.entityType === "person" ? User : Building2;

  const baseOpacity = data.active ? 1 : 0.45;
  const opacity = data.faded ? FADED_OPACITY : baseOpacity;

  return (
    <Box
      sx={{
        position: "relative",
        width: RELATED_W,
        height: RELATED_H,
        px: "10px",
        py: "6px",
        borderRadius: "6px",
        border: data.focused ? "2px solid" : "1px solid",
        borderColor: data.focused ? "primary.main" : "var(--mui-palette-divider)",
        background: entityStyle.background,
        opacity,
        boxShadow: data.focused
          ? "0 0 0 2px rgba(var(--mui-palette-primary-mainChannel) / 0.18)"
          : "none",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        outline: "none",
        cursor: data.relatedCompanyId ? "pointer" : "default",
        transition: "opacity 120ms, box-shadow 120ms, border-color 120ms",
      }}
    >
      <Handle id="top-s" type="source" position={Position.Top} style={{ opacity: 0, pointerEvents: "none" }} />
      <Handle id="top-t" type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: "none" }} />
      <Handle id="bottom-s" type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: "none" }} />
      <Handle id="bottom-t" type="target" position={Position.Bottom} style={{ opacity: 0, pointerEvents: "none" }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Icon sx={{ fontSize: "0.8rem", color: entityStyle.iconColor, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 600,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {data.name}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          mt: 0.25,
        }}
      >
        {data.roleTitle}
      </Typography>
      {data.ownershipPct && (
        <Typography
          variant="caption"
          sx={{
            color: data.ownershipPctColor,
            fontWeight: 700,
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          {data.ownershipPct}
        </Typography>
      )}
      {data.focused && data.relatedCompanyId && data.onNavigate && (
        <Tooltip title="Go to company">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              data.onNavigate?.();
            }}
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "primary.main",
              "&:hover": { bgcolor: "primary.main", color: "primary.contrastText" },
            }}
          >
            <ArrowRight sx={{ fontSize: "0.85rem" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function DividerNode({ data }: NodeProps<Node<DividerNodeData>>) {
  return (
    <Box
      sx={{
        width: data.width,
        height: 2,
        borderBottom: "2px dotted",
        borderColor: "rgba(var(--mui-palette-text-primaryChannel) / 0.35)",
        pointerEvents: "none",
      }}
    />
  );
}

const nodeTypes = { focal: FocalNode, related: RelatedNode, divider: DividerNode };

function sortByOwnership(edges: CompanyEdge[]): CompanyEdge[] {
  return [...edges].sort((a, b) => {
    const af = a.ownership_fraction ?? -1;
    const bf = b.ownership_fraction ?? -1;
    return bf - af;
  });
}

function nodesPerRowFor(chartW: number): number {
  return Math.max(1, Math.floor((chartW + H_GAP) / (RELATED_W + H_GAP)));
}

function subRowsFor(count: number, perRow: number): number {
  return Math.max(1, Math.ceil(count / perRow));
}

function bandHeight(count: number, perRow: number): number {
  const rows = subRowsFor(count, perRow);
  return rows * RELATED_H + (rows - 1) * INTRA_GAP;
}

// Distribute `count` nodes of width `nodeW` across `chartW`, returning the x for `idx`.
function distributeX(count: number, idx: number, nodeW: number, chartW: number): number {
  if (count <= 1) return (chartW - nodeW) / 2;
  const step = Math.min(nodeW + H_GAP, (chartW - nodeW) / (count - 1));
  const totalSpan = step * (count - 1);
  const startX = (chartW - totalSpan - nodeW) / 2;
  return startX + idx * step;
}

// Place `count` items in a band starting at topY, wrapping into multiple sub-rows
// of at most `perRow` items each. Returns each item's {x, y}.
function placeBand(
  count: number,
  topY: number,
  perRow: number,
  chartW: number,
): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  if (count === 0) return out;
  const totalRows = subRowsFor(count, perRow);
  for (let i = 0; i < count; i++) {
    const r = Math.floor(i / perRow);
    const c = i % perRow;
    const inThisRow = r === totalRows - 1 ? count - r * perRow : perRow;
    const x = distributeX(inThisRow, c, RELATED_W, chartW);
    const y = topY + r * (RELATED_H + INTRA_GAP);
    out.push({ x, y });
  }
  return out;
}

function addTreeNodes(
  treeNodes: OwnershipTreeNode[],
  direction: "down" | "up",
  flowNodes: Node[],
  flowEdges: Edge[],
  chartW: number,
  bandTopY: Map<number, number>,
): void {
  const byDepth = new Map<number, OwnershipTreeNode[]>();
  for (const n of treeNodes) {
    const list = byDepth.get(n.depth) ?? [];
    list.push(n);
    byDepth.set(n.depth, list);
  }

  const perRow = nodesPerRowFor(chartW);

  for (const [depth, nodes] of byDepth.entries()) {
    const topY = bandTopY.get(depth);
    if (topY == null) continue;
    const positions = placeBand(nodes.length, topY, perRow, chartW);

    nodes.forEach((n, i) => {
      const nodeId = `${direction}-tree-${n.company_id}`;
      const pct =
        n.cumulative_fraction != null
          ? `${(n.cumulative_fraction * 100).toFixed(1)}%`
          : null;

      const roleTitle =
        direction === "down"
          ? depth === 1 ? "Subsidiary" : "Indirect subsidiary"
          : depth === 1 ? "Shareholder" : "Indirect shareholder";

      flowNodes.push({
        id: nodeId,
        type: "related",
        position: positions[i],
        data: {
          name: n.company_name,
          roleTitle,
          active: true,
          ownershipPct: pct,
          ownershipPctColor: depth === 1 ? "success.main" : "warning.main",
          relatedCompanyId: n.company_id,
          entityType: "company" as const,
        },
        style: { width: RELATED_W, height: RELATED_H },
        selectable: true,
        draggable: false,
      });

      const parentNodeId: string | null =
        n.parent_company_id == null
          ? null
          : depth === 1
            ? "focal"
            : `${direction}-tree-${n.parent_company_id}`;

      if (!parentNodeId) return;

      if (direction === "down") {
        // parent (above) → this node (below)
        flowEdges.push({
          id: `tree-e-down-${n.company_id}`,
          source: parentNodeId,
          sourceHandle: "bottom-s",
          target: nodeId,
          targetHandle: "top-t",
          type: "bezier",
          style: { stroke: "var(--mui-palette-warning-main)", strokeWidth: 1.5 },
        });
      } else {
        // this node (shareholder, above) → parent company (below)
        flowEdges.push({
          id: `tree-e-up-${n.company_id}`,
          source: nodeId,
          sourceHandle: "bottom-s",
          target: parentNodeId,
          targetHandle: "top-t",
          type: "bezier",
          style: { stroke: "var(--mui-palette-warning-main)", strokeWidth: 1.5 },
        });
      }
    });
  }
}

export default function CompanyRelationsGraph({
  edges,
  companyName,
  companyId,
}: CompanyRelationsGraphProps) {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const [showDownTree, setShowDownTree] = useState(false);
  const [showUpTree, setShowUpTree] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartW, setChartW] = useState(800);

  const downTreeQuery = useQuery({
    queryKey: keys.masterCompany.ownershipTree(companyId, "down"),
    queryFn: () => api.getOwnershipTree(companyId, "down"),
    enabled: showDownTree,
  });

  const upTreeQuery = useQuery({
    queryKey: keys.masterCompany.ownershipTree(companyId, "up"),
    queryFn: () => api.getOwnershipTree(companyId, "up"),
    enabled: showUpTree,
  });

  const downTreeNodes = downTreeQuery.data?.nodes ?? [];
  const upTreeNodes = upTreeQuery.data?.nodes ?? [];

  // Suppress direct SHAREHOLDER_OF edges once tree data is loaded to avoid duplicates
  const inbound = sortByOwnership(
    edges.filter((e) => {
      if (e.direction !== "inbound") return false;
      if (
        showUpTree &&
        upTreeQuery.data != null &&
        e.relation_type === "SHAREHOLDER_OF" &&
        e.related_company_id != null
      )
        return false;
      return true;
    }),
  ).slice(0, 20);

  const outbound = edges
    .filter((e) => {
      if (e.direction !== "outbound") return false;
      if (showDownTree && downTreeQuery.data != null && e.relation_type === "SHAREHOLDER_OF")
        return false;
      return true;
    })
    .slice(0, 15);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const width = el.getBoundingClientRect().width;
    if (width > 0) setChartW(width);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (width > 0) setChartW(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const perRow = nodesPerRowFor(chartW);

  const upRows =
    showUpTree && upTreeQuery.data != null && upTreeNodes.length > 0
      ? Math.max(0, ...upTreeNodes.map((n) => n.depth))
      : 0;
  const downRows =
    showDownTree && downTreeQuery.data != null && downTreeNodes.length > 0
      ? Math.max(0, ...downTreeNodes.map((n) => n.depth))
      : 0;

  const upCountAt = (depth: number) =>
    upTreeNodes.filter((n) => n.depth === depth).length;
  const downCountAt = (depth: number) =>
    downTreeNodes.filter((n) => n.depth === depth).length;

  const showInboundBand = inbound.length > 0 && upRows === 0;
  const showOutboundBand = outbound.length > 0 && downRows === 0;

  // Build bands top → bottom; track each band's top Y as we stack them.
  type BandKey =
    | { kind: "up"; depth: number }
    | { kind: "inbound" }
    | { kind: "focal" }
    | { kind: "outbound" }
    | { kind: "down"; depth: number };

  const bands: Array<{ key: BandKey; height: number }> = [];
  for (let d = upRows; d >= 1; d--) {
    const c = upCountAt(d);
    if (c > 0) bands.push({ key: { kind: "up", depth: d }, height: bandHeight(c, perRow) });
  }
  if (showInboundBand) {
    bands.push({ key: { kind: "inbound" }, height: bandHeight(inbound.length, perRow) });
  }
  bands.push({ key: { kind: "focal" }, height: FOCAL_H });
  if (showOutboundBand) {
    bands.push({ key: { kind: "outbound" }, height: bandHeight(outbound.length, perRow) });
  }
  for (let d = 1; d <= downRows; d++) {
    const c = downCountAt(d);
    if (c > 0) bands.push({ key: { kind: "down", depth: d }, height: bandHeight(c, perRow) });
  }

  const upBandTopY = new Map<number, number>();
  const downBandTopY = new Map<number, number>();
  let inboundTopY = 0;
  let outboundTopY = 0;
  let focalY = TOP_PAD;
  let cursorY = TOP_PAD;
  const dividerYs: number[] = [];
  bands.forEach((b, i) => {
    if (i > 0) {
      dividerYs.push(cursorY + DEPTH_GAP / 2);
      cursorY += DEPTH_GAP;
    }
    const topY = cursorY;
    if (b.key.kind === "up") upBandTopY.set(b.key.depth, topY);
    else if (b.key.kind === "down") downBandTopY.set(b.key.depth, topY);
    else if (b.key.kind === "inbound") inboundTopY = topY;
    else if (b.key.kind === "outbound") outboundTopY = topY;
    else if (b.key.kind === "focal") focalY = topY;
    cursorY += b.height;
  });

  const treeActive = showDownTree || showUpTree;

  const graphH = cursorY + 40;

  const flowNodes: Node[] = [];
  const flowEdges: Edge[] = [];

  flowNodes.push({
    id: "focal",
    type: "focal",
    position: { x: (chartW - FOCAL_W) / 2, y: focalY },
    data: { label: companyName },
    style: { width: FOCAL_W, height: FOCAL_H },
    selectable: false,
    draggable: false,
  });

  if (showInboundBand) {
    const positions = placeBand(inbound.length, inboundTopY, perRow, chartW);
    inbound.forEach((e, i) => {
      const nodeId = `in-${e.edge_id}`;
      flowNodes.push({
        id: nodeId,
        type: "related",
        position: positions[i],
        data: {
          name: nodeName(e),
          roleTitle: formatRelationType(e.relation_type, e.relation_subtype),
          active: e.is_active,
          ownershipPct: directOwnershipPct(e),
          ownershipPctColor: "success.main",
          relatedCompanyId: e.related_company_id,
          entityType: e.related_contact_id != null ? "person" : "company",
        },
        style: { width: RELATED_W, height: RELATED_H },
        selectable: !!e.related_company_id,
        draggable: false,
      });
      flowEdges.push({
        id: `fe-${e.edge_id}`,
        source: nodeId,
        sourceHandle: "bottom-s",
        target: "focal",
        targetHandle: "top-t",
        type: "bezier",
        style: { stroke: edgeColor(e.relation_type), strokeWidth: 1.5 },
      });
    });
  }

  if (showOutboundBand) {
    const positions = placeBand(outbound.length, outboundTopY, perRow, chartW);
    outbound.forEach((e, i) => {
      const nodeId = `out-${e.edge_id}`;
      flowNodes.push({
        id: nodeId,
        type: "related",
        position: positions[i],
        data: {
          name: nodeName(e),
          roleTitle: formatRelationType(e.relation_type, e.relation_subtype),
          active: e.is_active,
          ownershipPct: directOwnershipPct(e),
          ownershipPctColor: "success.main",
          relatedCompanyId: e.related_company_id,
          entityType: "company" as const,
        },
        style: { width: RELATED_W, height: RELATED_H },
        selectable: !!e.related_company_id,
        draggable: false,
      });
      flowEdges.push({
        id: `fe-${e.edge_id}`,
        source: "focal",
        sourceHandle: "bottom-s",
        target: nodeId,
        targetHandle: "top-t",
        type: "bezier",
        style: { stroke: edgeColor(e.relation_type), strokeWidth: 1.5 },
      });
    });
  }

  if (showDownTree && downTreeNodes.length > 0) {
    addTreeNodes(downTreeNodes, "down", flowNodes, flowEdges, chartW, downBandTopY);
  }
  if (showUpTree && upTreeNodes.length > 0) {
    addTreeNodes(upTreeNodes, "up", flowNodes, flowEdges, chartW, upBandTopY);
  }

  dividerYs.forEach((y, i) => {
    flowNodes.push({
      id: `divider-${i}`,
      type: "divider",
      position: { x: 0, y: y - 1 },
      data: { width: chartW },
      style: { width: chartW, height: 2, pointerEvents: "none" as const },
      selectable: false,
      draggable: false,
      focusable: false,
    });
  });

  // Focus mode: highlight the clicked node's ownership chain in both
  // directions through focal. The relations graph is a tree rooted at
  // focal, with shareholders on the "up" side and subsidiaries on the
  // "down" side. From a clicked node we walk:
  //   - its same-side chain: ancestors up to focal + its own descendants
  //   - focal's opposite side: every node on the other side of focal
  // So clicking a shareholder shows its up-chain + focal + every
  // subsidiary; clicking a subsidiary shows its down-chain + focal +
  // every shareholder. Siblings on the same side stay faded.
  if (focusedId) {
    const parentOf = new Map<string, string>();
    for (const e of inbound) parentOf.set(`in-${e.edge_id}`, "focal");
    for (const e of outbound) parentOf.set(`out-${e.edge_id}`, "focal");
    for (const n of upTreeNodes) {
      const id = `up-tree-${n.company_id}`;
      parentOf.set(
        id,
        n.depth === 1 ? "focal" : `up-tree-${n.parent_company_id}`,
      );
    }
    for (const n of downTreeNodes) {
      const id = `down-tree-${n.company_id}`;
      parentOf.set(
        id,
        n.depth === 1 ? "focal" : `down-tree-${n.parent_company_id}`,
      );
    }
    const childrenOf = new Map<string, string[]>();
    for (const [child, parent] of parentOf) {
      const arr = childrenOf.get(parent) ?? [];
      arr.push(child);
      childrenOf.set(parent, arr);
    }

    const lineage = new Set<string>([focusedId]);
    let cur: string | undefined = parentOf.get(focusedId);
    while (cur) {
      lineage.add(cur);
      cur = parentOf.get(cur);
    }
    const stack = [focusedId];
    while (stack.length) {
      const top = stack.pop()!;
      for (const c of childrenOf.get(top) ?? []) {
        if (!lineage.has(c)) {
          lineage.add(c);
          stack.push(c);
        }
      }
    }

    const onUpSide =
      focusedId.startsWith("in-") || focusedId.startsWith("up-tree-");
    const onDownSide =
      focusedId.startsWith("out-") || focusedId.startsWith("down-tree-");
    if (onUpSide) {
      for (const e of outbound) lineage.add(`out-${e.edge_id}`);
      for (const n of downTreeNodes) lineage.add(`down-tree-${n.company_id}`);
    } else if (onDownSide) {
      for (const e of inbound) lineage.add(`in-${e.edge_id}`);
      for (const n of upTreeNodes) lineage.add(`up-tree-${n.company_id}`);
    }

    for (const n of flowNodes) {
      if (n.type === "divider") continue;
      const inFocus = lineage.has(n.id);
      const data = n.data as RelatedNodeData | FocalNodeData;
      data.faded = !inFocus;
      if (n.type === "related") {
        const rd = data as RelatedNodeData;
        rd.focused = n.id === focusedId;
        if (rd.focused && rd.relatedCompanyId) {
          rd.onNavigate = () => navigate(tenantPath(`/companies/${rd.relatedCompanyId}`));
        }
      }
    }
    for (const e of flowEdges) {
      const inFocus = lineage.has(e.source) && lineage.has(e.target);
      e.style = {
        ...e.style,
        opacity: inFocus ? 1 : FADED_OPACITY,
      };
    }
  }

  const isLoading =
    (showDownTree && downTreeQuery.isLoading) ||
    (showUpTree && upTreeQuery.isLoading);

  const interactive = fullscreen;

  const graphContent = (
    <Box
      sx={
        fullscreen
          ? { display: "flex", flexDirection: "column", height: "100%", p: 2 }
          : undefined
      }
    >
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showDownTree}
              onChange={(e) => setShowDownTree(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Show full ownership tree (down)</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showUpTree}
              onChange={(e) => setShowUpTree(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Show shareholder chain (up)</Typography>}
        />
        {isLoading && <CircularProgress size={16} />}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={fullscreen ? "Exit fullscreen" : "Expand"}>
          <IconButton size="small" onClick={() => setFullscreen((f) => !f)}>
            {fullscreen ? <X fontSize="small" /> : <Maximize2 fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        ref={containerRef}
        sx={{
          height: fullscreen ? "auto" : graphH,
          flexGrow: fullscreen ? 1 : undefined,
          minHeight: 0,
          border: treeActive || fullscreen ? "1px solid" : "none",
          borderColor: "divider",
          borderRadius: 1,
          "& .react-flow__attribution": { display: "none" },
        }}
      >
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={nodeTypes}
          fitView={fullscreen}
          fitViewOptions={fullscreen ? { padding: 0.1, maxZoom: 1 } : undefined}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={interactive}
          zoomOnScroll={interactive}
          zoomOnPinch={interactive}
          zoomOnDoubleClick={false}
          preventScrolling={interactive}
          proOptions={{ hideAttribution: true }}
          onNodeClick={(_event, node) => {
            if (node.type !== "related") return;
            const cid = (node.data as RelatedNodeData).relatedCompanyId;
            if (!cid) return;
            setFocusedId((cur) => (cur === node.id ? null : node.id));
          }}
          onPaneClick={() => setFocusedId(null)}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {!fullscreen && graphContent}
      <Dialog open={fullscreen} onClose={() => setFullscreen(false)} fullScreen>
        {graphContent}
      </Dialog>
    </>
  );
}
